/* ============================================================================
   BUILD YOURS — the interaction layer
   2026-09-18 · pairs with patchistry-builder-ios.css

   Four behaviours, all of which are presentation over the existing builder
   rather than a replacement for it. The filter engine, cart sync and tray
   writer in patchistry-builder-v3.js / patchistry2.liquid are untouched and
   remain the source of truth; this file only decides what is on screen.

     1. the flow       one page: explicit guides navigate between sections;
                       the selected hat stays in the sticky bar
     2. segmented      one indicator that slides, six leading categories,
                       everything else behind a sheet
     3. pinned bar     the filter track frosts once it sticks
     4. the throw      tapping a patch sends it to the tray

   It is deliberately defensive: the chips are built asynchronously by another
   script, so everything here re-applies on mutation instead of running once.
   ========================================================================= */
(function () {
  'use strict';

  var LEAD = ['All', 'New Drops', 'Moms', 'Bachelorette', 'Unhinged'];
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function labelOf(chip) { return (chip.textContent || '').trim(); }

  /* ── 1. the flow ───────────────────────────────────────────────────────────
     One page. The guide provides deliberate navigation. Selection changes
     update the sticky hat chip without moving the shopper's reading position. */
  function initFlow(root) {
    var canvas = root.querySelector('.pcr-canvas');
    var patches = root.querySelector('.pcr-hybrid-patch-intro') || root.querySelector('.pcr-grid');
    var track = root.querySelector('.pcr-controls');
    if (!track) return;

    each(root.querySelectorAll('[data-pcr-guide]'), function (button) {
      button.addEventListener('click', function () {
        var key = button.dataset.pcrGuide;
        var target = key === 'hat' ? canvas : key === 'look' ? root.querySelector('[data-pcr-tray].is-visible') || patches : patches;
        if (!target) return;
        if (key === 'look' && target.matches('[data-pcr-tray]')) { var link = target.querySelector('[data-pcr-edit-cart]'); if (link) link.focus(); }
        else window.scrollTo({ top: Math.max(0, target.getBoundingClientRect().top + window.scrollY - 100), behavior: reduced ? 'auto' : 'smooth' });
      });
    });

    // the hat chip lives at the head of the bar that is already sticky
    var chip = root.querySelector('.pcr-hatchip');
    if (!chip) {
      chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pcr-hatchip';
      chip.innerHTML = '<img alt="" aria-hidden="true"><span></span>';
      chip.addEventListener('click', function () {
        if (!canvas) return;
        window.scrollTo({
          top: Math.max(0, canvas.getBoundingClientRect().top + window.scrollY - 84),
          behavior: reduced ? 'auto' : 'smooth'
        });
      });
      track.insertBefore(chip, track.firstChild);
    }

    function selectedCard() {
      return root.querySelector('.pcr-canvas-card.is-selected, .pcr-canvas-card[aria-pressed="true"]');
    }
    function keyOf(el) {
      return el ? (el.getAttribute('data-variant-id') || el.getAttribute('data-title') || 'y') : '';
    }

    function paintChip() {
      var el = selectedCard();
      if (!el) { chip.classList.remove('is-on'); return; }
      var src = el.getAttribute('data-image-src') || '';
      var img = chip.querySelector('img');
      if (src && img.getAttribute('src') !== src) img.setAttribute('src', src);
      // "The Canvas — Black" is the product title; the colour is the choice
      var title = el.getAttribute('data-title') || '';
      var colour = title.split('\u2014').pop().trim() || title;
      chip.querySelector('span').textContent = colour;
      chip.setAttribute('aria-label', 'Hat: ' + colour + '. Change hat.');
      chip.classList.add('is-on');
    }

    var lastSeen = keyOf(selectedCard());
    paintChip();

    function watch() {
      var el = selectedCard();
      var now = keyOf(el);
      if (now === lastSeen) return;
      lastSeen = now;
      paintChip();
    }
    setInterval(watch, 220);
    new MutationObserver(watch).observe(root, {
      attributes: true, subtree: true, attributeFilter: ['class', 'aria-pressed']
    });

    // "See inside" — the under-brim print, on a deliberate tap rather than on
    // hover, so the photo on screen is the hat as it is worn unless asked.
    each(root.querySelectorAll('.pcr-canvas-card'), function (card) {
      var brim = card.querySelector('.pcr-canvas-card__under-brim');
      var shell = card.querySelector('.pcr-canvas-card__image');
      var tile = card.closest('.pcr-hat-tile');
      if (!brim || !shell || (tile || shell).querySelector('.pcr-inside-toggle')) return;
      var btn = document.createElement(tile ? 'button' : 'span');
      btn.className = 'pcr-inside-toggle';
      if (tile) { btn.type = 'button'; btn.setAttribute('aria-pressed', 'false'); }
      else { btn.setAttribute('role', 'button'); btn.setAttribute('tabindex', '0'); }
      btn.textContent = 'See inside';
      var flip = function (e) {
        // the whole card selects a hat, so this must not reach it
        e.preventDefault();
        e.stopPropagation();
        var on = card.classList.toggle('is-inside');
        btn.textContent = on ? 'See outside' : 'See inside';
        if (tile) btn.setAttribute('aria-pressed', String(on));
      };
      btn.addEventListener('click', flip, true);
      btn.addEventListener('keydown', function (e) {
        if (!tile && (e.key === 'Enter' || e.key === ' ')) flip(e);
      });
      (tile || shell).appendChild(btn);
    });
  }

  /* ── 2. the segmented control ──────────────────────────────────────────── */
  function initSegmented(root) {
    var track = root.querySelector('.pcr-controls');
    var indicator = root.querySelector('[data-pcr-seg-indicator]');
    var sheet = root.querySelector('[data-pcr-sheet]');
    var sheetList = root.querySelector('[data-pcr-sheet-list]');
    if (!track || !indicator) return;

    var moreBtn = null;
    var sheetOpen = false;
    var closeTimer = null;
    var openFrame = null;
    var previousOverflow = '';
    var backgroundState = [];

    function focusableRows() {
      return sheetList ? Array.prototype.filter.call(sheetList.querySelectorAll('button'), function (row) {
        return !row.disabled && row.getClientRects().length;
      }) : [];
    }

    function focusSheet(label) {
      var rows = focusableRows();
      var target = rows.filter(function (row) { return label && labelOf(row) === label; })[0] ||
                   rows.filter(function (row) { return row.getAttribute('aria-pressed') === 'true'; })[0] || rows[0];
      if (target) target.focus({ preventScroll: true });
    }

    function setBackgroundInert(inert) {
      if (inert) {
        // Only siblings along the dialog's ancestor path: never inert its own ancestor.
        var branch = sheet;
        while (branch && branch.parentElement) {
          each(branch.parentElement.children, function (sibling) {
            if (sibling !== branch) {
              backgroundState.push({ element: sibling, inert: sibling.inert });
              sibling.inert = true;
            }
          });
          if (branch.parentElement === document.body) break;
          branch = branch.parentElement;
        }
      } else {
        backgroundState.forEach(function (state) { state.element.inert = state.inert; });
        backgroundState = [];
      }
    }

    function returnFocus() {
      var target = moreBtn && !moreBtn.hidden ? moreBtn :
                   track.querySelector('.pcr-filter.is-active:not(.is-overflow):not(.pcr-filter--more)') ||
                   track.querySelector('.pcr-filter:not(.is-overflow):not(.pcr-filter--more)') ||
                   track.querySelector('.pcr-hatchip.is-on');
      if (target && target.getClientRects().length) target.focus({ preventScroll: true });
    }

    function openSheet(open) {
      if (!sheet) return;
      if (closeTimer !== null) clearTimeout(closeTimer);
      if (openFrame !== null) cancelAnimationFrame(openFrame);
      closeTimer = openFrame = null;
      if (open) {
        if (!sheetOpen) {
          previousOverflow = document.body.style.overflow;
          setBackgroundInert(true);
        }
        sheetOpen = true;
        sheet.hidden = false;
        document.body.style.overflow = 'hidden';
        openFrame = requestAnimationFrame(function () {
          openFrame = null;
          if (!sheetOpen) return;
          sheet.classList.add('is-open');
          focusSheet();
        });
      } else {
        if (!sheetOpen) return;
        sheetOpen = false;
        sheet.classList.remove('is-open');
        setBackgroundInert(false);
        document.body.style.overflow = previousOverflow;
        returnFocus();
        closeTimer = setTimeout(function () {
          closeTimer = null;
          if (!sheetOpen) sheet.hidden = true;
        }, 400);
      }
      if (moreBtn) moreBtn.setAttribute('aria-expanded', String(sheetOpen));
    }

    function moveIndicator() {
      var active = track.querySelector('.pcr-filter.is-active:not(.is-overflow)') ||
                   track.querySelector('.pcr-filter:not(.is-overflow)');
      if (!active) { indicator.classList.remove('is-ready'); return; }
      var t = track.getBoundingClientRect(), a = active.getBoundingClientRect();
      // While step 1 is on screen the whole wall is display:none, so every chip
      // measures zero. Placing the indicator from that reading leaves it
      // invisible for the rest of the session — wait for a real layout instead.
      if (!a.width) { indicator.classList.remove('is-ready'); return; }
      indicator.style.width = a.width + 'px';
      indicator.style.transform = 'translate3d(' + (a.left - t.left) + 'px,0,0)';
      // the first placement must not animate from 0, or the bar slides in on load
      requestAnimationFrame(function () { indicator.classList.add('is-ready'); });
    }

    // apply() reorders and appends chips, and a MutationObserver on the track
    // calls apply() — so without this it re-enters itself forever and the page
    // never finishes rendering.
    var applying = false;
    var applyFrame = null;

    function scheduleApply() {
      if (applyFrame !== null) return;
      applyFrame = requestAnimationFrame(function () { applyFrame = null; apply(); });
    }

    function apply() {
      if (applying) return;
      applying = true;
      try { applyInner(); } finally { applying = false; }
    }

    function applyInner() {
      if (!track.clientWidth) return;
      var chips = track.querySelectorAll('.pcr-filter:not(.pcr-filter--more)');
      if (!chips.length) return;

      var overflow = [];
      each(chips, function (chip) {
        // The script that builds these sets a photo as an inline background.
        // An inline style beats any stylesheet rule that is not !important, and
        // this stylesheet deliberately has none — so the tile is removed at the
        // source instead. The photos are also what made the labels unreadable.
        if (chip.style.backgroundImage) chip.style.backgroundImage = '';
        var lead = LEAD.indexOf(labelOf(chip)) !== -1;
        chip.classList.toggle('is-overflow', !lead);
        if (!lead) overflow.push(chip);
      });

      // nothing gained by hiding two chips behind a sheet
      if (overflow.length < 3) {
        each(chips, function (c) { c.classList.remove('is-overflow'); });
        overflow = [];
      }

      // Put the leading chips in Brian's priority order rather than the order
      // the catalogue happens to emit them. This matters because the fit loop
      // drops from the end: left as-is, a narrow phone lost Moms — his
      // strongest-selling audience — while keeping Unhinged.
      //
      // Only move anything when the order is actually wrong. Every DOM move
      // fires the track's own MutationObserver, which calls back into here, so
      // an unconditional reorder re-enters forever and the page never settles.
      var want = LEAD.filter(function (label) {
        return Array.prototype.some.call(chips, function (c) {
          return labelOf(c) === label && !c.classList.contains('is-overflow');
        });
      });
      var have = Array.prototype.map.call(
        track.querySelectorAll('.pcr-filter:not(.pcr-filter--more)'), labelOf
      ).filter(function (l) { return want.indexOf(l) !== -1; });
      if (want.join('>') !== have.join('>')) {
        want.forEach(function (label) {
          for (var i = 0; i < chips.length; i++) {
            if (labelOf(chips[i]) === label) { track.appendChild(chips[i]); break; }
          }
        });
      }

      // More has to exist before the row is measured, because it takes width too
      function ensureMore() {
        if (moreBtn) return;
        moreBtn = document.createElement('button');
        moreBtn.type = 'button';
        moreBtn.className = 'pcr-filter pcr-filter--more';
        moreBtn.setAttribute('aria-haspopup', 'dialog');
        moreBtn.setAttribute('aria-expanded', 'false');
        moreBtn.appendChild(document.createTextNode('More'));
        moreBtn.addEventListener('click', function () { openSheet(true); });
        track.appendChild(moreBtn);
      }
      if (overflow.length) ensureMore();
      if (moreBtn) moreBtn.hidden = !overflow.length;
      if (moreBtn && track.lastElementChild !== moreBtn) track.appendChild(moreBtn);

      // Then make it fit. Brian ruled out sideways scrolling, so the control has
      // to be honest about its own width: drop the last leading category into
      // the sheet until the row stops overflowing. On a 393px phone that
      // typically leaves All + two + More; on a desktop all six stay.
      // read the LIVE order, not the captured NodeList — the chips were just
      // reordered, so dropping "the last one" has to mean the last one shown
      var lead = Array.prototype.filter.call(
        track.querySelectorAll('.pcr-filter:not(.pcr-filter--more)'),
        function (c) { return !c.classList.contains('is-overflow'); }
      );
      var guard = 0;
      while (track.scrollWidth > track.clientWidth + 1 && guard++ < chips.length) {
        // All and the hat remain available. New Drops may also need to spill
        // at 320px; keeping two categories was what clipped More.
        var candidates = lead.filter(function (c) { return labelOf(c) !== 'All'; });
        if (!candidates.length) break;
        var dropped = candidates[candidates.length - 1];
        lead.splice(lead.indexOf(dropped), 1);
        dropped.classList.add('is-overflow');
        overflow.unshift(dropped);
        ensureMore();
        moreBtn.hidden = false;
      }

      // the sheet mirrors the hidden chips and clicks them, so the filter engine
      // stays the only thing that knows how filtering works
      if (sheetList) {
        var hadFocus = sheetOpen && sheetList.contains(document.activeElement);
        var focusedLabel = hadFocus ? labelOf(document.activeElement) : null;
        sheetList.textContent = '';
        overflow.forEach(function (chip) {
          var row = document.createElement('button');
          row.type = 'button';
          row.className = 'pcr-sheet__item';
          row.setAttribute('aria-pressed', String(chip.classList.contains('is-active')));
          row.appendChild(document.createTextNode(labelOf(chip)));
          row.addEventListener('click', function () {
            chip.click();
            openSheet(false);
          });
          sheetList.appendChild(row);
        });
        if (sheetOpen && !overflow.length) openSheet(false);
        else if (hadFocus) focusSheet(focusedLabel);
      }

      // a category chosen from the sheet still has to read as chosen up top
      var activeHidden = track.querySelector('.pcr-filter.is-active.is-overflow');
      if (moreBtn) moreBtn.classList.toggle('is-active', !!activeHidden);
      moveIndicator();
    }

    if (sheet) {
      each(sheet.querySelectorAll('[data-pcr-sheet-close]'), function (el) {
        el.addEventListener('click', function () { openSheet(false); });
      });
      document.addEventListener('keydown', function (e) {
        if (!sheetOpen) return;
        if (e.key === 'Escape') { e.preventDefault(); openSheet(false); return; }
        if (e.key !== 'Tab') return;
        var rows = focusableRows();
        if (!rows.length) { e.preventDefault(); return; }
        var index = rows.indexOf(document.activeElement);
        if (index === -1 || (e.shiftKey && index === 0) || (!e.shiftKey && index === rows.length - 1)) {
          e.preventDefault();
          rows[e.shiftKey ? rows.length - 1 : 0].focus({ preventScroll: true });
        }
      });
    }

    track.addEventListener('click', function (e) {
      if (e.target.closest('.pcr-filter')) setTimeout(apply, 30);
    });
    new MutationObserver(function () { apply(); }).observe(track, { childList: true });
    window.addEventListener('resize', scheduleApply);
    // the wall only has a layout once its step is on screen
    new MutationObserver(function () {
      if (root.dataset.step === '2') scheduleApply();
    }).observe(root, { attributes: true, attributeFilter: ['data-step'] });
    var lastTrackWidth = 0;
    if (window.ResizeObserver) new ResizeObserver(function () {
      var width = track.clientWidth;
      if (width !== lastTrackWidth) { lastTrackWidth = width; if (width) scheduleApply(); }
    }).observe(track)

    // The hat chip mounts hidden and only appears once a hat is chosen, which
    // re-flows the whole strip AFTER the last measurement. Measured before this
    // fix: the indicator sat 110px from the active segment (on the chip), and
    // "More" was pushed 34px past the edge of a track with overflow:hidden —
    // putting 11 categories out of reach. Re-run the whole fit when it changes.
    var chip = root.querySelector('.pcr-hatchip')
    if (chip) {
      new MutationObserver(scheduleApply)
        .observe(chip, { attributes: true, attributeFilter: ['class'] })
      if (window.ResizeObserver) new ResizeObserver(scheduleApply).observe(chip)
    };

    apply();
    setTimeout(apply, 600);   // the chips are built after first paint
    setTimeout(apply, 1800);
  }

  /* ── 3. the bar frosts when it pins ────────────────────────────────────── */
  function initPinned(root) {
    var track = root.querySelector('.pcr-controls');
    if (!track || !('IntersectionObserver' in window)) return;
    var sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;height:1px;width:1px;';
    track.parentNode.insertBefore(sentinel, track);
    new IntersectionObserver(function (entries) {
      track.classList.toggle('is-pinned', !entries[0].isIntersecting);
    }, { rootMargin: '-72px 0px 0px 0px', threshold: 0 }).observe(sentinel);
  }

  /* ── 4. the throw ──────────────────────────────────────────────────────── */
  function initThrow(root) {
    if (reduced) return;
    // document-level capture, for the same reason the step listener is: the
    // builder stops click propagation before it reaches the section
    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) return;
      var card = e.target.closest('[data-pcr-card]');
      if (!card || card.classList.contains('is-sold-out')) return;
      // only on the way in; removing a patch should not launch anything
      if (card.classList.contains('is-selected')) return;

      var art = card.querySelector('.pcr-patch-img');
      var target = root.querySelector('[data-pcr-thumbs]') || root.querySelector('.pcr-tray');
      if (!art || !target) return;

      var from = art.getBoundingClientRect();
      var to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;

      var ghost = art.cloneNode();
      ghost.className = 'pcr-fly';
      ghost.style.left = from.left + 'px';
      ghost.style.top = from.top + 'px';
      ghost.style.width = from.width + 'px';
      ghost.style.height = from.height + 'px';
      document.body.appendChild(ghost);

      var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
      var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
      requestAnimationFrame(function () {
        ghost.style.transform = 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(0.18)';
        ghost.style.opacity = '0.15';
      });
      setTimeout(function () { ghost.remove(); }, 460);
    }, true);
  }

  /* ── 5. the tray ───────────────────────────────────────────────────────── */
  function trayGlimpseItems(items) {
    var shown = [];
    ['hat', 'patch'].forEach(function (kind) {
      var first = items.find(function (item) { return item.kind === kind; });
      if (first) shown.push(first);
    });
    items.forEach(function (item) {
      if (shown.length < 2 && shown.indexOf(item) < 0) shown.push(item);
    });
    return { shown: shown, more: items.reduce(function (sum, item) {
      return sum + (shown.indexOf(item) < 0 ? item.quantity : 0);
    }, 0) };
  }

  function decorateTrayCheckout(checkout) {
    if (checkout.querySelector('[data-pcr-action-icon]')) return;
    var ns = 'http://www.w3.org/2000/svg';
    var icon = document.createElementNS(ns, 'svg');
    icon.setAttribute('data-pcr-action-icon', '');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.setAttribute('viewBox', '0 0 16 16');
    icon.setAttribute('width', '14');
    icon.setAttribute('height', '14');
    var path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M3 8h10m-4-4 4 4-4 4');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.5');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    icon.appendChild(path);
    checkout.appendChild(icon);
  }

  function initTray(root) {
    var tray = root.querySelector('[data-pcr-tray]');
    if (!tray) return;

    // A stray inline style painted the shipping note in cream — invisible on a
    // light bar, and inline beats a stylesheet that carries no !important.
    each(tray.querySelectorAll('[style]'), function (el) {
      if (/rgba\(245\s*,\s*239\s*,\s*228/.test(el.getAttribute('style') || '')) {
        el.style.color = '';
      }
    });

    // The page reserved 72px of bottom padding for a bar that measures 155,
    // so the last row of patches sat underneath it. The 72 comes from
    // patchistry-tray-v5.js, which keeps rewriting --pcr-tray-h on this same
    // element — so publishing the real number under that name is a fight that
    // gets overwritten. Use our own variable and leave theirs alone.
    function publishHeight() {
      root.style.setProperty('--b-tray-h', Math.ceil(tray.getBoundingClientRect().height) + 'px');
    }
    if (window.ResizeObserver) new ResizeObserver(publishHeight).observe(tray);
    publishHeight();
    window.addEventListener('resize', publishHeight);

    // Presentation state is separate from the cart writer's .is-open state.
    // The details remain visible if JavaScript fails; only this explicit button
    // collapses them, leaving totals and checkout outside the hidden region.
    var toggle = tray.querySelector('[data-pcr-tray-toggle]');
    var details = tray.querySelector('[data-pcr-tray-details]');
    if (!toggle || !details) return;
    // Move existing nodes once, retaining cart listeners, preview controls and
    // focus identity. The legacy center wrapper remains for tray upgrade checks.
    var hats = tray.querySelector('.pcr-tray__hats');
    var thumbs = tray.querySelector('[data-pcr-thumbs]');
    var addHat = tray.querySelector('[data-pcr-add-hat]');
    var meter = tray.querySelector('[data-pcr-progress]');
    if (hats && thumbs) {
      var selections = document.createElement('div');
      selections.className = 'pcr-tray__selections';
      selections.appendChild(hats);
      selections.appendChild(thumbs);
      if (addHat) selections.appendChild(addHat);
      details.appendChild(selections);
      if (meter) details.appendChild(meter);
    }
    var count = tray.querySelector('[data-pcr-tray-count]');
    var glimpse = toggle.querySelector('[data-pcr-tray-glimpse]');
    var discount = tray.querySelector('[data-pcr-actual-discount]');
    var meta = tray.querySelector('.pcr-tray__meta');
    // Keep Shopify's confirmed savings node; shorten only its display wording.
    if (discount && meta) {
      meta.appendChild(discount);
      function formatSavings() {
        var value = discount.textContent || '';
        if (/ cart savings applied$/.test(value)) discount.textContent = 'Saving ' + value.replace(/ cart savings applied$/, '');
      }
      new MutationObserver(formatSavings).observe(discount, { childList: true, characterData: true, subtree: true });
      formatSavings();
    }
    // The confirmed cart renderer replaces this link's textContent. Restore only
    // its decorative icon; readiness, href, tabindex and label remain renderer-owned.
    var checkout = tray.querySelector('[data-pcr-checkout]');
    if (checkout && checkout.querySelector) {
      function restoreCheckoutIcon() { decorateTrayCheckout(checkout); }
      new MutationObserver(restoreCheckoutIcon).observe(checkout, { childList: true });
      restoreCheckoutIcon();
    }
    var glimpseSignature = '';
    function updateGlimpse() {
      if (!glimpse || !hats || !thumbs) return;
      var items = [];
      each(hats.querySelectorAll('.pcr-hat-slot'), function (item) {
        var img = item.querySelector('img'), badge = item.querySelector('span');
        if (img) items.push({ kind: 'hat', src: img.src, quantity: Number(badge && badge.textContent) || 1 });
      });
      each(thumbs.querySelectorAll('.pcr-tray__thumb'), function (item) {
        var img = item.querySelector('img'), badge = item.querySelector('.pcr-tray__thumb-qty');
        if (img) items.push({ kind: 'patch', src: img.src, quantity: Number(badge && badge.textContent) || 1 });
      });
      var summary = trayGlimpseItems(items);
      var signature = JSON.stringify(summary);
      if (signature !== glimpseSignature) {
        glimpseSignature = signature;
        glimpse.replaceChildren();
        summary.shown.forEach(function (item) {
          var wrap = document.createElement('span'), img = document.createElement('img');
          wrap.className = 'pcr-tray__glimpse-item';
          img.src = item.src; img.alt = ''; img.width = 36; img.height = 36;
          wrap.appendChild(img);
          if (item.quantity > 1) {
            var qty = document.createElement('span'); qty.className = 'pcr-tray__glimpse-qty'; qty.textContent = String(item.quantity); wrap.appendChild(qty);
          }
          glimpse.appendChild(wrap);
        });
        if (summary.more) {
          var more = document.createElement('span'); more.className = 'pcr-tray__more'; more.textContent = '+' + summary.more; glimpse.appendChild(more);
        }
      }
      toggle.setAttribute('aria-label', (details.hidden ? 'Show' : 'Hide') + ' details' + (count ? ': ' + count.textContent : '') + (summary.more ? '; +' + summary.more + ' more' : ''));
    }
    if (glimpse && hats && thumbs) {
      var glimpseObserver = new MutationObserver(updateGlimpse);
      [hats, thumbs, count].filter(Boolean).forEach(function (node) {
        glimpseObserver.observe(node, { childList: true, characterData: true, subtree: true });
      });
    }
    var compact = window.matchMedia('(max-width: 899px)');
    var expanded = false;

    function syncDisclosure() {
      var keyboard = tray.classList.contains('is-keyboard-open');
      var hide = compact.matches && (!expanded || keyboard);
      if (hide && details.contains(document.activeElement)) toggle.focus({ preventScroll: true });
      if (!compact.matches && document.activeElement === toggle) {
        var next = details.querySelector('button:not(:disabled), a[href]') || tray.querySelector('[data-pcr-edit-cart]');
        // Reveal before focusing when a collapsed phone layout becomes desktop.
        details.hidden = false;
        if (next) next.focus({ preventScroll: true });
      }
      toggle.hidden = !compact.matches;
      toggle.disabled = keyboard;
      toggle.setAttribute('aria-expanded', String(!hide));
      toggle.setAttribute('aria-label', hide ? 'Show details' : 'Hide details');
      details.hidden = hide;
      updateGlimpse();
      publishHeight();
    }
    toggle.addEventListener('click', function () {
      expanded = !expanded;
      syncDisclosure();
    });
    tray.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !compact.matches || !expanded ||
          !(details.contains(event.target) || event.target === toggle)) return;
      event.preventDefault();
      expanded = false;
      toggle.focus({ preventScroll: true });
      syncDisclosure();
    });
    compact.addEventListener('change', function () {
      // Never make a focused thumbnail disappear just because the view narrows.
      if (compact.matches && details.contains(document.activeElement)) expanded = true;
      syncDisclosure();
    });
    new MutationObserver(syncDisclosure).observe(tray, { attributes: true, attributeFilter: ['class'] });
    syncDisclosure();
  }

  function initPatchTools(root) {
    var dialog = document.createElement('dialog');
    var previewTrigger = null;
    dialog.className = 'pcr-patch-preview';
    dialog.innerHTML = '<button type="button" class="pcr-preview-close" aria-label="Close preview">×</button><img alt=""><h2></h2><a>View patch details</a>';
    dialog.querySelector('h2').id = root.id + '-patch-preview-title';
    dialog.setAttribute('aria-labelledby', root.id + '-patch-preview-title');
    root.appendChild(dialog);
    dialog.querySelector('button').addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('click', function(event) { if(event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', function () {
      var trigger = previewTrigger;
      previewTrigger = null;
      if (!trigger) return;
      if (!trigger.isConnected) {
        var variant = String(trigger.dataset.variantId);
        trigger = Array.prototype.find.call(root.querySelectorAll('[data-pcr-tray-preview]'), function (item) { return String(item.dataset.variantId) === variant; })
          || Array.prototype.find.call(root.querySelectorAll('[data-pcr-card]'), function (item) { return String(item.dataset.variantId) === variant && !item.closest('.pcr-product-tile')?.hidden; })
          || root.querySelector('[data-pcr-edit-cart]');
      }
      var hiddenDetails = trigger && trigger.closest('[data-pcr-tray-details][hidden]');
      if (hiddenDetails) trigger = root.querySelector('[data-pcr-tray-toggle]');
      if (trigger) trigger.focus({ preventScroll: true });
    });
    each(root.querySelectorAll('.pcr-patch-tools'), function(tools) {
      tools.addEventListener('click', function(event) {
        var tile = tools.closest('.pcr-product-tile'), card = tile.querySelector('[data-pcr-card]');
        var quantity = event.target.closest('[data-pcr-quantity-delta]');
        if(quantity) { event.preventDefault(); window.__patchistryCartSync?.changePatchQuantity(root, card, Number(quantity.dataset.pcrQuantityDelta)); return; }
      });
    });
    // Tray thumbnails are rebuilt after cart confirmation, so delegate here.
    root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-pcr-tray-preview]');
      if (!trigger || !root.contains(trigger)) return;
      var card = Array.prototype.find.call(root.querySelectorAll('[data-pcr-card]'), function (item) {
        return String(item.dataset.variantId) === String(trigger.dataset.variantId);
      });
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();
      var image = card.querySelector('.pcr-patch-img');
      dialog.querySelector('img').src = image ? image.currentSrc || image.src : '';
      dialog.querySelector('img').alt = card.dataset.title || '';
      dialog.querySelector('h2').textContent = card.dataset.title || 'Your patch';
      dialog.querySelector('a').href = card.dataset.productUrl || '#';
      previewTrigger = trigger;
      dialog.showModal();
    });
  }

  function boot() {
    each(document.querySelectorAll('[id^="PatchistryCameraRoll-"]'), function (root) {
      if (root.dataset.iosReady === '1') return;
      if (root.dataset.pcrBuilderMode === 'badge-reel') return;
      root.dataset.iosReady = '1';
      // Each behaviour is independent, so one failing must not take the others
      // with it — a thrown error in the filter code should never cost you the
      // step flow or the tray. Whatever fails is named on the element, which is
      // also how this was diagnosed in the first place.
      var failed = [];
      [['flow', initFlow], ['segmented', initSegmented], ['pinned', initPinned],
       ['throw', initThrow], ['tray', initTray], ['patchTools', initPatchTools]].forEach(function (pair) {
        try { pair[1](root); } catch (e) {
          failed.push(pair[0] + ':' + (e && e.message ? e.message : e));
        }
      });
      if (failed.length) root.dataset.iosFailed = failed.join(' | ');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
